import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ENV_AUTH0_AUDIENCE } from "../env";
import { useAlertNotifications } from "@/components/Alert/alertManager";
import { AUTH0_DUPLICATE_PHONE_NUMBER_ERROR } from "@/constants/profile";
import {
  CONTENT_TYPE_APPLICATION_JSON,
  HTTP_HEADER_CONTENT_TYPE,
  HTTP_STATUS_NO_CONTENT,
} from "@/constants/http";

interface ManagementApiOptions extends RequestInit {
  parseJson?: boolean;
  message?: string;
  suppressErrorAlert?: boolean;
}

const SESSION_EXPIRED_ERROR_CODES = new Set([
  "invalid_grant",
  "login_required",
  "missing_refresh_token",
  "invalid_refresh_token",
]);

function getAuthErrorCode(error: unknown) {
  if (!error || typeof error !== "object") {
    return "";
  }

  const { error: code, code: alternateCode } = error as Record<string, unknown>;

  if (typeof code === "string" && code.trim()) {
    return code;
  }

  if (typeof alternateCode === "string" && alternateCode.trim()) {
    return alternateCode;
  }

  return "";
}

function isSessionExpiredAuthError(error: unknown) {
  return SESSION_EXPIRED_ERROR_CODES.has(getAuthErrorCode(error));
}

function extractApiErrorMessage(payload: unknown) {
  if (!payload || typeof payload !== "object") {
    return "";
  }

  const candidateKeys = ["message", "error_description", "error", "detail"];
  for (const key of candidateKeys) {
    const value = (payload as Record<string, unknown>)[key];
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }

  return "";
}

function mapApiErrorMessage(apiMessage: string) {
  const normalizedMessage = apiMessage.trim().toLowerCase();
  if (normalizedMessage === AUTH0_DUPLICATE_PHONE_NUMBER_ERROR) {
    return "managementApi.errors.phoneNumberExists";
  }
  return "";
}

export function useAuth0ManagementApi() {
  const { t } = useTranslation();
  const { showErrorAlert } = useAlertNotifications();
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const callManagementApi = useCallback(
    async <T = unknown>(url: string, options: ManagementApiOptions = {}): Promise<T | undefined> => {
      const {
        parseJson = true,
        headers: inputHeaders,
        message,
        suppressErrorAlert = false,
        ...requestInit
      } = options;
const notifyError = (errorMessage: string) => {
  if (!suppressErrorAlert) {
    showErrorAlert(errorMessage);
  }
};

      const redirectToLogin = async () => {
        await loginWithRedirect({
          appState: {
            returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
          },
        });
      };

      let token: string;
      try {
        token = await getAccessTokenSilently({
          authorizationParams: { audience: ENV_AUTH0_AUDIENCE },
        });
      } catch (error) {
        if (isSessionExpiredAuthError(error)) {
          await redirectToLogin();
          return undefined;
        }

        notifyError(t("managementApi.errors.authenticate"));
        throw error;
      }

      if (!token) {
        await redirectToLogin();
        return undefined;
      }

      const headers = new Headers(inputHeaders);
      headers.set("Authorization", `Bearer ${token}`);
      if (!headers.has(HTTP_HEADER_CONTENT_TYPE)) {
        headers.set(HTTP_HEADER_CONTENT_TYPE, CONTENT_TYPE_APPLICATION_JSON);
      }

      let response: Response;
      try {
        response = await fetch(url, {
          ...requestInit,
          headers,
        });
      } catch (error) {
        notifyError(t("managementApi.errors.network"));
        throw error;
      }

      if (!response.ok) {
        let apiMessage = "";

        try {
          const contentType = response.headers.get("Content-Type") ?? "";
          if (contentType.toLowerCase().includes("application/json")) {
            const payload = (await response.json()) as unknown;
            apiMessage = extractApiErrorMessage(payload);
          } else {
            const textPayload = await response.text();
            if (textPayload.trim()) {
              apiMessage = textPayload.trim();
            }
          }
        } catch {
          // Ignore body parsing failures and use fallback status message.
        }

        const fallbackMessage = t("managementApi.errors.requestFailed", {
          status: response.status,
        });
        const mappedApiMessageKey = apiMessage
          ? mapApiErrorMessage(apiMessage)
          : "";
        const mappedApiMessage = mappedApiMessageKey
          ? t(mappedApiMessageKey)
          : "";
        const finalMessage = mappedApiMessage || message || apiMessage || fallbackMessage;
        notifyError(finalMessage);
        throw new Error(
          mappedApiMessage ||
            apiMessage ||
            t("managementApi.errors.fetchFailed", {
              status: response.status,
            }),
        );
      }

      if (!parseJson || response.status === HTTP_STATUS_NO_CONTENT) {
        return undefined;
      }

      try {
        return (await response.json()) as T;
      } catch (error) {
        notifyError(t("managementApi.errors.responseParse"));
        throw error;
      }
    },
    [getAccessTokenSilently, loginWithRedirect, showErrorAlert, t],
  );

  return { callManagementApi };
}
